/**
 * Atendimento do período (#557): volume, canais, horários e pessoas.
 *
 * ── O mapa por hora é de demanda ──────────────────────────────────────────
 *
 * Cada célula é quantas mensagens os clientes mandaram naquela hora daquele
 * dia da semana, no fuso da organização. A intensidade é relativa à célula
 * mais cheia do período; o número exato vai no nome acessível e no título da
 * célula, para quem não lê pela cor. O mapa é laranja — o acento, a cor do que
 * merece o olho —, e o pico vem escrito em cima dele.
 *
 * ── Cada bloco diz de quem são os números ─────────────────────────────────
 *
 * O volume é da organização inteira, como no painel (a barra de recorte diz);
 * só o bloco por pessoa se recorta pela carteira, e o cartão dele diz isso.
 */

import { useState, type CSSProperties, type ReactNode } from 'react';
import type { AtendimentoDoPeriodo } from '@cliqcrm/contracts';
import { useAtendimentoDoPeriodo } from '@/shared/api/consultas';
import { Estado } from '@/shared/ui/estados/estados';
import { Avatar, CabecalhoDeTela, Icone } from '@/shared/ui';
import { iconeDoCanal, nomeDoCanal } from '@/entities/atendimento/canal';
import {
  diaPorExtenso,
  diasDoPeriodo,
  espera,
  PERIODO_PADRAO,
  type ChaveDePeriodo,
} from '../lib/periodo';
import { BarraDoRelatorio, TituloDoCartao } from './barra';
import { ABRANGENCIA, porcentagem } from '../lib/formato';
import { BotaoExportar } from './exportar';
import { nomeDaPlanilha, planilhaDoAtendimento } from '../lib/planilhas';
import './relatorios.css';

const DIAS_DA_SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const DIAS_POR_EXTENSO = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];
const HORAS = Array.from({ length: 24 }, (_, h) => h);

const numero = (n: number): string => new Intl.NumberFormat('pt-BR').format(n);
const mensagens = (n: number): string => `${numero(n)} ${n === 1 ? 'mensagem' : 'mensagens'}`;

export function AtendimentoTela(): ReactNode {
  const [periodo, definirPeriodo] = useState<ChaveDePeriodo>(PERIODO_PADRAO);
  const consulta = useAtendimentoDoPeriodo({ dias: String(diasDoPeriodo(periodo)) });

  return (
    <div className="relatorio">
      <CabecalhoDeTela
        titulo="Atendimento"
        apoio="Volume por canal, horário e pessoa no período."
        acoes={
          <BotaoExportar
            planilha={
              consulta.data
                ? () => ({
                    conteudo: planilhaDoAtendimento(consulta.data!),
                    nome: nomeDaPlanilha('atendimento', consulta.data!.periodo),
                  })
                : null
            }
          />
        }
        {...(consulta.data
          ? {
              meta: (
                <span>
                  {diaPorExtenso(consulta.data.periodo.de)} a{' '}
                  {diaPorExtenso(consulta.data.periodo.ate)}
                </span>
              ),
            }
          : {})}
        extra={
          <BarraDoRelatorio
            periodo={periodo}
            aoEscolherPeriodo={definirPeriodo}
            rotuloDaAbrangencia="Volume de"
            {...(consulta.data
              ? { abrangencia: ABRANGENCIA[consulta.data.abrangencia_do_volume] }
              : {})}
          />
        }
      />

      <div className="relatorio__corpo">
        <Estado consulta={consulta} forma="cartoes" linhas={3}>
          {(dados) => (
            <>
              <Resumo dados={dados} />
              <div className="relatorio__par">
                <PorCanal dados={dados} />
                <MapaPorHora dados={dados} />
              </div>
              <PorPessoa dados={dados} />
            </>
          )}
        </Estado>
      </div>
    </div>
  );
}

function Resumo({ dados }: { dados: AtendimentoDoPeriodo }): ReactNode {
  const { resumo } = dados;
  const itens: { nome: string; valor: string; apoio: string; destaque?: boolean }[] = [
    { nome: 'Atendimentos', valor: numero(resumo.atendimentos), apoio: 'no período' },
    {
      nome: 'Resolvidas',
      valor: numero(resumo.resolvidas),
      apoio:
        resumo.atendimentos === 0
          ? 'nenhum atendimento'
          : `${porcentagem(resumo.resolvidas, resumo.atendimentos)} dos atendimentos`,
    },
    {
      nome: 'Sem resposta',
      valor: numero(resumo.sem_resposta),
      apoio: 'atendimentos sem resposta',
      destaque: resumo.sem_resposta > 0,
    },
    {
      nome: 'Espera típica',
      valor: espera(resumo.espera_mediana_segundos) ?? '—',
      apoio: 'pela primeira resposta',
    },
    {
      nome: 'Espera dos mais demorados',
      valor: espera(resumo.espera_p90_segundos) ?? '—',
      apoio: 'os 10% que mais esperaram',
    },
  ];
  return (
    <section aria-labelledby="atendimento-resumo">
      <h2 id="atendimento-resumo" className="visualmente-oculto">
        Resumo
      </h2>
      <ul className="indicadores">
        {itens.map((item) => (
          <li
            key={item.nome}
            className={
              item.destaque ? 'indicadores__item indicadores__item--destaque' : 'indicadores__item'
            }
          >
            <span className="indicadores__nome">{item.nome}</span>
            <span className="indicadores__valor">{item.valor}</span>
            <span className="indicadores__apoio">{item.apoio}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function PorCanal({ dados }: { dados: AtendimentoDoPeriodo }): ReactNode {
  const maior = Math.max(1, ...dados.por_canal.map((c) => c.atendimentos));
  return (
    <section className="relatorio__cartao" aria-labelledby="atendimento-canais">
      <TituloDoCartao id="atendimento-canais" apoio="atendimentos · espera típica">
        Por canal
      </TituloDoCartao>
      {dados.por_canal.length === 0 ? (
        <p className="relatorio__vazio">Nenhum atendimento no período.</p>
      ) : (
        <ul className="barras">
          {dados.por_canal.map((canal, indice) => {
            const glifo = iconeDoCanal(canal.canal);
            return (
              <li key={canal.canal} className="barras__linha">
                <span className="barras__glifo" data-canal={glifo ?? undefined}>
                  {glifo && <Icone nome={glifo} />}
                </span>
                <span className="barras__nome">{nomeDoCanal(canal.canal) ?? canal.canal}</span>
                <span className="barras__valor">
                  <span className="barras__numero">{numero(canal.atendimentos)} atendimentos</span>
                  {canal.espera_mediana_segundos !== null &&
                    ` · espera ${espera(canal.espera_mediana_segundos)}`}
                </span>
                <span className="barras__trilho" aria-hidden="true">
                  <span
                    className={
                      indice === 0
                        ? 'barras__preenchido barras__preenchido--maior'
                        : 'barras__preenchido'
                    }
                    style={{ width: `${(canal.atendimentos / maior) * 100}%` }}
                  />
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function MapaPorHora({ dados }: { dados: AtendimentoDoPeriodo }): ReactNode {
  const porCelula = new Map(
    dados.por_hora.map((c) => [`${c.dia_semana}-${c.hora}`, c.mensagens_recebidas]),
  );
  const maior = Math.max(1, ...dados.por_hora.map((c) => c.mensagens_recebidas));
  const pico = dados.por_hora.reduce<AtendimentoDoPeriodo['por_hora'][number] | null>(
    (melhor, c) => (c.mensagens_recebidas > (melhor?.mensagens_recebidas ?? 0) ? c : melhor),
    null,
  );

  return (
    <section
      className="relatorio__cartao relatorio__cartao--largo"
      aria-labelledby="atendimento-horas"
    >
      <TituloDoCartao
        id="atendimento-horas"
        {...(pico
          ? {
              apoio: (
                <>
                  Pico:{' '}
                  <strong className="relatorio__pico">
                    {DIAS_POR_EXTENSO[pico.dia_semana]}, {pico.hora}h
                  </strong>{' '}
                  · {mensagens(pico.mensagens_recebidas)}
                </>
              ),
            }
          : {})}
      >
        Quando os clientes escrevem
      </TituloDoCartao>
      <div className="relatorio__rolagem">
        <table className="mapa-de-horas">
          <caption className="visualmente-oculto">
            Mensagens recebidas por dia da semana e hora do dia
          </caption>
          <thead>
            <tr>
              <th scope="col" />
              {HORAS.map((h) => (
                <th key={h} scope="col" className="mapa-de-horas__hora">
                  {h % 3 === 0 ? `${h}h` : <span className="visualmente-oculto">{h}h</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {DIAS_DA_SEMANA.map((dia, d) => (
              <tr key={dia}>
                <th scope="row" className="mapa-de-horas__dia">
                  {dia}
                </th>
                {HORAS.map((h) => {
                  const quantas = porCelula.get(`${d}-${h}`) ?? 0;
                  const texto = `${DIAS_POR_EXTENSO[d]}, ${h}h: ${quantas} ${quantas === 1 ? 'mensagem' : 'mensagens'}`;
                  return (
                    <td key={h} className="mapa-de-horas__celula" title={texto}>
                      <span
                        className="mapa-de-horas__cor"
                        style={{ opacity: quantas === 0 ? 0 : 0.15 + 0.85 * (quantas / maior) }}
                        aria-hidden="true"
                      />
                      <span className="visualmente-oculto">{texto}</span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mapa-de-horas__legenda" aria-hidden="true">
        <span>menos</span>
        {[0, 0.35, 0.65, 1].map((intensidade) => (
          <span
            key={intensidade}
            className="mapa-de-horas__amostra"
            style={{ '--intensidade': intensidade } as CSSProperties}
          />
        ))}
        <span>mais</span>
        <span className="mapa-de-horas__nota">
          No fuso da organização · passe o mouse para ver o número
        </span>
      </div>
    </section>
  );
}

function PorPessoa({ dados }: { dados: AtendimentoDoPeriodo }): ReactNode {
  return (
    <section className="relatorio__cartao" aria-labelledby="atendimento-pessoas">
      <TituloDoCartao
        id="atendimento-pessoas"
        abrangencia={ABRANGENCIA[dados.por_pessoa.abrangencia]}
      >
        Por pessoa
      </TituloDoCartao>
      {dados.por_pessoa.itens.length === 0 ? (
        <p className="relatorio__vazio">Ninguém da equipe atendeu pelo CliqCRM no período.</p>
      ) : (
        <div className="relatorio__tabela">
          <table>
            <caption className="visualmente-oculto">Atendimento por pessoa</caption>
            <thead>
              <tr>
                <th scope="col">Pessoa</th>
                <th scope="col" className="relatorio__num">
                  Conversas com ela agora
                </th>
                <th scope="col" className="relatorio__num">
                  Mensagens enviadas
                </th>
              </tr>
            </thead>
            <tbody>
              {dados.por_pessoa.itens.map((pessoa) => (
                <tr key={pessoa.usuario_id}>
                  <td>
                    <span className="relatorio__pessoa relatorio__pessoa--fixa">
                      <Avatar nome={pessoa.nome ?? '?'} tamanho={28} foto={pessoa.foto_url} />
                      <span>{pessoa.nome ?? 'Sem nome'}</span>
                    </span>
                  </td>
                  <td className="relatorio__num">{numero(pessoa.conversas)}</td>
                  <td className="relatorio__num">{numero(pessoa.mensagens_enviadas)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="relatorio__nota">
        Conversas que começaram no período e estão com a pessoa agora; mensagens que ela enviou pelo
        CliqCRM.
      </p>
    </section>
  );
}
